// Applies the daisyUI style choices picked on /design to the game's markup.
//
// Elements opt in with `data-ds="<component id>"`. Their class list is replaced
// with the chosen daisyUI classes, so nothing in the templates has to know about
// variants:
//
//   <button data-ds="button">Play</button>
//
// The choice comes from the published theme (`styles`), with per-component
// defaults so the game still looks right before anything is picked.

import type { StyleChoice } from "./theme";
import {
  COMPONENTS,
  classesFor,
  varsFor,
  type ComponentSpec,
} from "./daisy-catalog";

/** Effective choice for a component: stored value, else the spec's default. */
export function resolveChoice(
  spec: ComponentSpec,
  styles: Record<string, StyleChoice>,
): StyleChoice {
  const stored = styles[spec.id];
  const defaults = spec.defaults ?? {};
  // A stored id from an older catalogue revision falls back to the default.
  const storedStyle = spec.variants.some((v) => v.id === stored?.style)
    ? stored?.style
    : "";
  return {
    style: storedStyle || defaults.style || spec.variants[0].id,
    color: stored?.color ?? defaults.color ?? "",
    size: stored?.size ?? defaults.size ?? "",
    shape: stored?.shape || defaults.shape || spec.shapes?.[0]?.id || "",
    options: { ...(defaults.options ?? {}), ...(stored?.options ?? {}) },
    image: stored?.image ?? "",
  };
}

/** Classes the game should put on an element of this component. */
export function classesForComponent(
  id: string,
  styles: Record<string, StyleChoice>,
  override: Partial<StyleChoice> = {},
): string | null {
  const spec = COMPONENTS.find((c) => c.id === id);
  if (!spec) return null;
  return classesFor(spec, { ...resolveChoice(spec, styles), ...override });
}

/** Classes + inline CSS custom properties (image URL, …) for an element. */
export function styleForComponent(
  id: string,
  styles: Record<string, StyleChoice>,
  override: Partial<StyleChoice> = {},
): { cls: string; vars: Record<string, string> } | null {
  const spec = COMPONENTS.find((c) => c.id === id);
  if (!spec) return null;
  const choice = { ...resolveChoice(spec, styles), ...override };
  return { cls: classesFor(spec, choice), vars: varsFor(spec, choice) };
}

let lastStyles: Record<string, StyleChoice> = {};

/** Custom properties we set on an element last time, so stale ones get cleared. */
const appliedVars = new WeakMap<HTMLElement, string[]>();

/**
 * Classes + vars for a component using the most recently applied choices.
 * For markup that is built outside `applyComponentStyles` (the cycle selects
 * are wrappers created around hidden native `<select>` elements).
 */
export function currentComponentStyle(
  id: string,
): { cls: string; vars: Record<string, string> } | null {
  return styleForComponent(id, lastStyles);
}

/**
 * Re-apply the last known choices. Call after building markup dynamically
 * (the menu renders its settings rows on demand).
 */
export function refreshComponentStyles(): void {
  applyComponentStyles(lastStyles);
}

/** Rewrite every `[data-ds]` element's class list from the current choices. */
export function applyComponentStyles(styles: Record<string, StyleChoice>): void {
  lastStyles = styles;
  for (const el of Array.from(document.querySelectorAll<HTMLElement>("[data-ds]"))) {
    applyOne(el, styles);
  }
}

/**
 * Opt a dynamically created element into a component and style it right away.
 *
 * Markup built after the theme pass (settings rows, editors) would otherwise
 * keep native UA styling until the next `applyComponentStyles` run. The
 * `data-ds*` attributes stay on the element, so a later theme change still
 * refreshes it. `keep` holds layout/behaviour classes the element needs (the
 * class list is replaced).
 */
export function dsElement(
  el: HTMLElement,
  id: string,
  opts: {
    color?: string;
    size?: string;
    style?: string;
    shape?: string;
    keep?: string;
  } = {},
): void {
  el.dataset.ds = id;
  if (opts.color) el.dataset.dsColor = opts.color;
  if (opts.size) el.dataset.dsSize = opts.size;
  if (opts.style) el.dataset.dsStyle = opts.style;
  if (opts.shape) el.dataset.dsShape = opts.shape;
  if (opts.keep) el.dataset.dsKeep = opts.keep;
  applyOne(el, lastStyles);
}

/** Apply the last known choices to one element. */
function applyOne(el: HTMLElement, styles: Record<string, StyleChoice>): void {
  const id = el.dataset.ds;
  if (!id) return;
  const override: Partial<StyleChoice> = {};
  if (el.dataset.dsStyle) override.style = el.dataset.dsStyle;
  if (el.dataset.dsColor) override.color = el.dataset.dsColor;
  if (el.dataset.dsSize) override.size = el.dataset.dsSize;
  if (el.dataset.dsShape) override.shape = el.dataset.dsShape;
  const style = styleForComponent(id, styles, override);
  if (style === null) return;
  // Classes the element needs for layout/behaviour (kept from the template).
  const keep = (el.dataset.dsKeep ?? "").split(/\s+/).filter(Boolean);
  // If the template pins a width/limit, it owns layout: drop the component's
  // own width option classes (the published theme may set, e.g., `w-56` for
  // inputs). Without this a compact row overflows its grid column.
  const ownsWidth = keep.some((c) => /^(w-|min-w-|max-w-)/.test(c));
  const classes = style.cls
    .split(/\s+/)
    .filter(Boolean)
    .filter((c) => !(ownsWidth && /^(w-|min-w-|max-w-)/.test(c)));
  el.className = [...keep, ...classes].join(" ");
  const previous = appliedVars.get(el) ?? [];
  for (const key of previous) {
    if (!(key in style.vars)) el.style.removeProperty(key);
  }
  for (const [key, value] of Object.entries(style.vars)) {
    el.style.setProperty(key, value);
  }
  appliedVars.set(el, Object.keys(style.vars));
}
